import React, { useState, useEffect } from 'react';
import { Tooltip } from 'devextreme-react/tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { applicationInsights } from '../../ApplicationInsights';
import { ExperienceApi } from '@apply/api';
import { LoadPanel } from 'devextreme-react/load-panel';
import DataGrid, { Column, Paging, Pager, Selection } from 'devextreme-react/data-grid';
import { formatDateTime } from '@apply/ui';
import { handleAdminError } from '../../common/SharedFunctions';

export interface SuggestedVerificationWebsiteOptions {
    id: number;
    stateCd: string;
    county: string;
    isOpen: boolean;
}

export interface SuggestedVerificationWebsiteLinkProps {
    options: SuggestedVerificationWebsiteOptions;
    onSubmit: (selectedItems: any) => void; // eslint-disable-line
    onCancel: () => void;
}

export const SuggestedVerificationWebsiteLinkModal: React.FC<SuggestedVerificationWebsiteLinkProps> = ({
    options,
    onSubmit,
    onCancel,
}) => {
    // const  = props;
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);

    const [suggestedItems, set_suggestedItems] = useState(null);
    const [selectedSuggestions, set_selectedSuggestions] = useState(null);
    const [loadingMessage, set_loadingMessage] = useState('');

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        formRow: { marginBottom: '0' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
    };

    const allowedPageSizes = [15, 25, 50];
    const defaultPageSize = 25;

    const loadSuggestedVerificationWebsiteLinks = async (criteria) => {
        try {
            set_loadingMessage('Loading...');

            const response = await experienceApi.getSuggestedVerificationWebsiteLinks(criteria);
            if (response.statusCode !== 200) {
                return;
            }

            const result = response.result;
            set_suggestedItems(result);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const resetState = () => {
        set_suggestedItems(null);
        set_selectedSuggestions(null);
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();
            if (!options.isOpen || !options.stateCd || !options.county || !options.id) {
                return;
            }
            //set criteria
            const model = getModel();
            await loadSuggestedVerificationWebsiteLinks(model);
        }

        isOpenChanged();
    }, [options.isOpen, options.stateCd, options.county]);

    const getModel = () => {
        return {
            state: options.stateCd,
            county: options.county,
        };
    };

    const onVerifiedDateRender = (e) => {
        if (e.value && e.value != '') {
            return formatDateTime(e.value);
        } else {
            return <span className='red-text'>No Verfication Status</span>;
        }
    };

    const onVerifiedByNameRender = (e) => {
        if (e.value && e.value != '') {
            return e.value;
        } else {
            return <span className='red-text'>No Verfication Status</span>;
        }
    };

    const onSourceWebsiteRender = (e) => {
        const targetId = `sugg_website_${e.data.id}}`;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.value}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {websiteTooltipText(e.value)}
                </Tooltip>
            </>
        );
    };

    const onSourceURLRender = (e) => {
        const targetId = `sugg_url_${e.data.id}}`;
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.value}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {urlTooltipText(e.value)}
                </Tooltip>
            </>
        );
    };

    const splitURL = (value: string) => {
        const splitURLS = value.split('\r');
        return splitURLS;
    };

    const splitWebsite = (value: string) => {
        const splitWebsites = value.split('\n');
        return splitWebsites;
    };

    const urlTooltipText = (url: string) => {
        if (!url) {
            return '';
        }
        return (
            <div style={{ textAlign: 'left' }}>
                {splitURL(url).map((element) => {
                    return <div>{element}</div>;
                })}
            </div>
        );
    };

    const websiteTooltipText = (website: string) => {
        if (!website) {
            return '';
        }
        return (
            <div style={{ textAlign: 'left' }}>
                {splitWebsite(website).map((element) => {
                    return <div>{element}</div>;
                })}
            </div>
        );
    };

    const onSelectionChanged = (e) => {
        set_selectedSuggestions(e.selectedRowsData);
    };

    const _onSubmit = () => {
        onSubmit(selectedSuggestions);
    };

    const _onCancel = () => {
        handleCancel();
    };

    const _handleClose = () => {
        handleCancel();
    };

    const handleCancel = () => {
        resetState();
        onCancel();
    };

    if (!options.isOpen || !suggestedItems) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={options.isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '1000px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>Suggested Verification Websites</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div
                                    className='scrollable-content'
                                    style={{ margin: '20px' }}
                                >
                                    <DataGrid
                                        dataSource={suggestedItems}
                                        showBorders={true}
                                        showRowLines={false}
                                        showColumnHeaders={true}
                                        rowAlternationEnabled={true}
                                        columnMinWidth={50}
                                        columnAutoWidth={false}
                                        allowSorting={true}
                                        remoteOperations={false}
                                        allowColumnResizing={true}
                                        noDataText='No Suggested Websites available'
                                        onSelectionChanged={onSelectionChanged}
                                    >
                                        <Selection
                                            mode='multiple'
                                            selectAllMode='allPages'
                                            showCheckBoxesMode='always'
                                        />
                                        <Column
                                            dataField='verifiedDate'
                                            caption='Verified On'
                                            width={150}
                                            alignment='left'
                                            cellRender={onVerifiedDateRender}
                                        />
                                        <Column
                                            dataField='verifiedByName'
                                            caption='Verified By'
                                            width={150}
                                            alignment='left'
                                            cellRender={onVerifiedByNameRender}
                                        />
                                        <Column
                                            dataField='sourceWebsite'
                                            caption='Website'
                                            width={150}
                                            alignment='left'
                                            cellRender={onSourceWebsiteRender}
                                        />
                                        <Column
                                            dataField='sourceUrl'
                                            caption='Source Link'
                                            alignment='left'
                                            cellRender={onSourceURLRender}
                                        />
                                        <Paging defaultPageSize={defaultPageSize} />
                                        <Pager
                                            showPageSizeSelector={allowedPageSizes.length > 1}
                                            allowedPageSizes={allowedPageSizes}
                                            showNavigationButtons={true}
                                            showInfo={true}
                                        />
                                    </DataGrid>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.buttonContainer}
                                >
                                    <button
                                        type='button'
                                        className='button primary'
                                        data-dismiss='modal'
                                        onClick={_onSubmit}
                                    >
                                        Import
                                    </button>
                                    <button
                                        type='button'
                                        style={{ marginLeft: '10px' }}
                                        className='button primary'
                                        data-dismiss='modal'
                                        onClick={_onCancel}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <LoadPanel
                    hideOnOutsideClick={false}
                    visible={!!loadingMessage}
                    message={loadingMessage}
                />
            </Dialog>
        </>
    );
};
